/**
 * PullToRefresh Component
 * Ermöglicht Pull-to-Refresh-Geste auf Touch-Geräten
 */

import React, { useState, useRef, useCallback, useEffect, type ReactNode } from 'react';

interface PullToRefreshProps {
  children: ReactNode;
  onRefresh: () => Promise<void>;
  /** Threshold in px to trigger refresh (default: 80) */
  threshold?: number;
  /** Max pull distance in px (default: 120) */
  maxPull?: number;
  /** Whether pull-to-refresh is enabled (default: true) */
  enabled?: boolean;
}

type RefreshState = 'idle' | 'pulling' | 'ready' | 'refreshing';

export function PullToRefresh({
  children,
  onRefresh,
  threshold = 80,
  maxPull = 120,
  enabled = true,
}: PullToRefreshProps): React.ReactElement {
  const [state, setState] = useState<RefreshState>('idle');
  const [pullDistance, setPullDistance] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const currentY = useRef(0);

  // Check if we're at the top of the page
  const isAtTop = useCallback(() => {
    if (!containerRef.current) return false;
    // Check both window scroll and container scroll
    const windowAtTop = window.scrollY <= 0;
    const containerAtTop = containerRef.current.scrollTop <= 0;
    return windowAtTop && containerAtTop;
  }, []);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enabled || state === 'refreshing') return;
      if (!isAtTop()) return;

      startY.current = e.touches[0].clientY;
      currentY.current = startY.current;
    },
    [enabled, state, isAtTop]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!enabled || state === 'refreshing') return;
      if (startY.current === 0) return;

      currentY.current = e.touches[0].clientY;
      const diff = currentY.current - startY.current;

      // Only trigger if pulling down and at top
      if (diff > 0 && isAtTop()) {
        // Prevent default scroll behavior when pulling
        e.preventDefault();

        // Apply resistance - pull distance decreases as you pull further
        const resistance = 0.5;
        const adjustedDiff = Math.min(diff * resistance, maxPull);

        setPullDistance(adjustedDiff);
        setState(adjustedDiff >= threshold ? 'ready' : 'pulling');
      } else {
        // Reset if scrolling up or not at top
        if (state !== 'idle') {
          setPullDistance(0);
          setState('idle');
        }
      }
    },
    [enabled, state, threshold, maxPull, isAtTop]
  );

  const handleTouchEnd = useCallback(async () => {
    if (!enabled) return;

    if (state === 'ready') {
      setState('refreshing');
      setPullDistance(threshold * 0.6); // Keep indicator visible during refresh

      try {
        await onRefresh();
      } finally {
        setState('idle');
        setPullDistance(0);
      }
    } else {
      setState('idle');
      setPullDistance(0);
    }

    startY.current = 0;
    currentY.current = 0;
  }, [enabled, state, threshold, onRefresh]);

  // Add touch event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    // Wrapper to handle async event handler
    const touchEndHandler = (): void => {
      void handleTouchEnd();
    };

    // Use passive: false to allow preventDefault in touchmove
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', touchEndHandler, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', touchEndHandler);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Calculate indicator opacity and rotation
  const indicatorOpacity = Math.min(pullDistance / threshold, 1);
  const indicatorRotation = (pullDistance / threshold) * 180;

  return (
    <div ref={containerRef} className="relative min-h-full">
      {/* Pull indicator */}
      <div
        className="absolute left-0 right-0 flex items-center justify-center overflow-hidden pointer-events-none z-50"
        style={{
          height: pullDistance,
          top: 0,
          transition: state === 'idle' ? 'height 0.2s ease-out' : 'none',
        }}
      >
        <div
          className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
            state === 'refreshing'
              ? 'bg-cherry-600'
              : state === 'ready'
                ? 'bg-cherry-600'
                : 'bg-charcoal-200'
          }`}
          style={{
            opacity: indicatorOpacity,
            transform: `translateY(${Math.max(0, pullDistance - 50)}px)`,
          }}
        >
          {state === 'refreshing' ? (
            // Spinning loader
            <svg
              className="w-5 h-5 text-white animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            // Arrow indicator
            <svg
              className={`w-5 h-5 transition-colors ${
                state === 'ready' ? 'text-white' : 'text-charcoal-500'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{
                transform: `rotate(${indicatorRotation}deg)`,
                transition: state === 'idle' ? 'transform 0.2s ease-out' : 'none',
              }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          )}
        </div>
      </div>

      {/* Content wrapper */}
      <div
        style={{
          transform: `translateY(${pullDistance}px)`,
          transition: state === 'idle' ? 'transform 0.2s ease-out' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default PullToRefresh;
